import React, { useState, useEffect } from 'react';
import { Joyride as JoyrideComponent, STATUS } from 'react-joyride';
import { useTheme } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import * as userService from '../services/userService';

// Type assertion for Joyride to work around TypeScript issues with v3
const Joyride = JoyrideComponent as unknown as React.ComponentType<any>;

const CURRENT_ONBOARDING_VERSION = 1;

const OnboardingTour: React.FC = () => {
  const theme = useTheme();
  const { user, refreshUserData } = useAuth();
  const [run, setRun] = useState(false);

  const steps = [
    {
      target: '[data-tour="chat-input"]',
      content: 'Use o chat para tirar dúvidas jurídicas. A IA vai sugerir os melhores documentos para o seu caso.',
      title: '💬 Chat com IA Jurídica',
      placement: 'bottom' as const,
    },
    {
      target: '[data-tour="shortcuts"]',
      content: 'Acesse rapidamente: gerar documentos, visualizar seus arquivos e configurar alertas.',
      title: '⚡ Ações Rápidas',
      placement: 'top' as const,
    },
    {
      target: '[data-tour="credits-widget"]',
      content: 'Acompanhe seus créditos disponíveis. Clique para ver detalhes ou adquirir mais.',
      title: '💳 Seus Créditos',
      placement: 'top' as const,
    },
  ];

  const shouldShowTour = Boolean(user && user.onboarding_completed_at === null);

  useEffect(() => {
    if (shouldShowTour && !run) {
      // Start tour immediately after component mounts
      const timer = setTimeout(() => {
        setRun(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldShowTour, run]);

  const handleCallback = async (data: { status: string }) => {
    const { status } = data;
    
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      try {
        await userService.completeOnboarding(CURRENT_ONBOARDING_VERSION);
        await refreshUserData();
      } catch (error) {
        console.error('Error completing onboarding:', error);
      }
    }
  };

  if (!shouldShowTour) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={true}
      showSkipButton={true}
      showProgress={true}
      scrollToFirstStep={true}
      spotlightClicks={true}
      onEvent={handleCallback}
      options={{
        skipBeacon: true,
        primaryColor: theme.palette.primary.main,
        zIndex: 10000,
        arrowColor: '#fff',
        backgroundColor: '#fff',
        overlayColor: 'rgba(0, 0, 0, 0.5)',
        textColor: '#333',
      }}
      floaterProps={{
        disableAnimation: true,
      }}
      locale={{
        back: 'Voltar',
        close: 'Fechar',
        last: 'Finalizar',
        next: 'Próximo',
        skip: 'Pular tour',
      }}
      styles={{
        tooltip: {
          borderRadius: 12,
          padding: 20,
        },
        tooltipTitle: {
          fontSize: 18,
          fontWeight: 700,
        },
        tooltipContent: {
          fontSize: 14,
          lineHeight: 1.6,
        },
        buttonNext: {
          borderRadius: 8,
          padding: '10px 20px',
          fontWeight: 600,
        },
        buttonBack: {
          borderRadius: 8,
          marginRight: 10,
        },
        buttonSkip: {
          color: '#666',
        },
      }}
    />
  );
};

export default OnboardingTour;
